import { useRef, useState, type ReactNode } from "react";
import { Popover } from "radix-ui";
import { Info } from "lucide-react";
import { cx } from "./cx";

export interface StatusPopoverProps {
  label: ReactNode;
  accessibleLabel?: string;
  children: ReactNode;
  className?: string;
}

export function StatusPopover({ label, accessibleLabel, children, className }: StatusPopoverProps) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const cancelClose = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
    }, 140);
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        className={cx(
          "-mx-1 inline-flex cursor-help items-center gap-[7px] whitespace-nowrap rounded-[5px] px-1 py-0.5 hover:bg-surface-3 aria-expanded:bg-surface-3",
          className,
        )}
        aria-label={accessibleLabel}
        onPointerEnter={(e) => {
          if (e.pointerType === "touch") return;
          cancelClose();
          setOpen(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === "touch") return;
          scheduleClose();
        }}
        onClick={(e) => {
          e.preventDefault();
          cancelClose();
          setOpen(true);
        }}
        onFocus={(e) => {
          if (e.currentTarget.matches(":focus-visible")) setOpen(true);
        }}
        onBlur={scheduleClose}
      >
        {label}
        <Info size={12} strokeWidth={1.8} aria-hidden="true" className="opacity-70" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
          onPointerEnter={cancelClose}
          onPointerLeave={scheduleClose}
          className="z-popover w-[310px] rounded-xl border border-border bg-bg px-3.5 py-3 text-ui leading-[1.45] text-fg shadow-pop [&_dd]:m-0 [&_dd]:text-fg [&_dl]:mt-2 [&_dl]:grid [&_dl]:grid-cols-[auto_1fr] [&_dl]:gap-x-2.5 [&_dl]:gap-y-[3px] [&_dl]:text-md [&_dt]:text-muted [&_p]:m-0 [&_p]:mb-1.5"
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function PopoverHeading({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="mb-1.5 flex items-center gap-[7px] font-semibold [&_svg]:size-3.5">
      {icon}
      {children}
    </div>
  );
}

export interface ClickPopoverProps {
  trigger: ReactNode;
  label?: string | undefined;
  contentLabel?: string;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: "top" | "bottom" | "left" | "right";
  asChild?: boolean;
}

export function ClickPopover({
  trigger,
  label,
  contentLabel,
  children,
  className,
  contentClassName,
  open,
  onOpenChange,
  side = "bottom",
  asChild = false,
}: ClickPopoverProps) {
  return (
    <Popover.Root {...(open === undefined ? {} : { open })} {...(onOpenChange ? { onOpenChange } : {})}>
      <Popover.Trigger
        asChild={asChild}
        aria-label={label}
        className={asChild ? undefined : cx("rounded-[4px] text-left", className)}
      >
        {trigger}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side={side}
          align="start"
          aria-label={contentLabel}
          sideOffset={6}
          collisionPadding={8}
          className={cx(
            "z-popover w-[320px] rounded-xl border border-border bg-bg px-3.5 py-3 text-ui leading-[1.45] text-fg shadow-pop [&_dd]:m-0 [&_dd]:text-fg [&_dl]:mt-2 [&_dl]:grid [&_dl]:grid-cols-[auto_1fr] [&_dl]:gap-x-2.5 [&_dl]:gap-y-[3px] [&_dl]:text-md [&_dt]:text-muted [&_p]:m-0 [&_p]:mb-1.5",
            contentClassName,
          )}
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function AnchoredPopover({
  anchor,
  open,
  onOpenChange,
  label,
  children,
}: {
  anchor: HTMLElement | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <Popover.Root open={open && anchor !== null} onOpenChange={onOpenChange}>
      <Popover.Anchor virtualRef={{ current: anchor ?? document.body }} />
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          aria-label={label}
          className="z-popover w-[300px] rounded-xl border border-border bg-bg px-3.5 py-3 text-ui leading-[1.45] text-fg shadow-pop [&_dd]:m-0 [&_dd]:text-fg [&_dl]:mt-2 [&_dl]:grid [&_dl]:grid-cols-[auto_1fr] [&_dl]:gap-x-2.5 [&_dl]:gap-y-[3px] [&_dl]:text-md [&_dt]:text-muted [&_p]:m-0 [&_p]:mb-1.5"
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
